import { SDKResponse } from '@commercetools/frontend-sdk';
import { CloudinaryProductAssetGroup } from 'shared/types/content';
import useSWR from 'swr';
import { sdk } from 'sdk';

const EMPTY: CloudinaryProductAssetGroup = { primary: [], additional: [] };

/**
 * Base product images for the currently-selected variant, cached by (product, image key).
 *
 * `imageKey` comes from `getImageCacheKey` and covers every attribute the image varies on for this
 * product — material and handrail height on a post, wood species on a tread — while leaving out the
 * spec-row attributes that ride along with a SKU without changing the render. Keying on it rather
 * than the SKU means a change that cannot affect the image is a cache hit, and every change that
 * can affect it refetches. The key is scoped by `productId` because axis values like "Walnut" or
 * "42 in" are shared across products; an unscoped key would let one product read another's cached
 * image.
 *
 * The fetch still uses the current `sku` because Cloudinary assets are tagged `product_id:<sku>`.
 *
 * `seed` is the server-provided group for the initial variant; it is shown immediately (via
 * `fallbackData`) so the first paint has no flash, and `keepPreviousData` holds the current image on
 * screen while a new key loads. `revalidateOnMount: true` states explicitly that the hook fetches
 * the current key once on mount, so the seed becomes a real cache entry rather than an ephemeral
 * per-render value and returning to an already-seen key is a hit.
 *
 * This hook does *not* spread `revalidateOptions`, so `revalidateIfStale` keeps SWR's default of
 * `true`. (An earlier version of this comment claimed the app disabled it globally; it does not —
 * `<SWRConfig>` is rendered without a value and only `useCart`/`useAccount` opt into those options.
 * The distinction matters: a cache entry that somehow holds a failed response is revalidated on the
 * next read rather than being stuck for the session.)
 */
export const useProductBaseImages = (
  productId: string | undefined,
  imageKey: string | undefined,
  sku: string | undefined,
  seed?: CloudinaryProductAssetGroup,
): CloudinaryProductAssetGroup => {
  const response = useSWR<SDKResponse<CloudinaryProductAssetGroup>>(
    productId && imageKey && sku ? ['action/assets/getProductAssetsBySku', productId, imageKey] : null,
    () =>
      sdk.callAction<CloudinaryProductAssetGroup>({
        actionName: 'assets/getProductAssetsBySku',
        query: { sku: sku as string },
      }),
    {
      fallbackData: seed
        ? ({ isError: false, tracing: { frontendRequestId: '' }, data: seed } as SDKResponse<CloudinaryProductAssetGroup>)
        : undefined,
      keepPreviousData: true,
      revalidateOnMount: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  if (response.data && !response.data.isError) return response.data.data;
  return seed ?? EMPTY;
};
